import React, { useCallback, useContext, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";
import { Link as RouterLink, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "react-query";

import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";

import PageHeader from "../../../components/PageHeader";
import SectionCard from "../../../components/SectionCard";
import DataTable from "../../../components/DataTable";
import StatusBadge from "../../../components/StatusBadge";
import CalculationTabs from "../../../components/CalculationTabs";
import {
    deleteCreatedOrder,
    getOrderByCalculation,
} from "../../../api/orderApi";
import { LanguageContext } from "../../../components/LanguageContext";

const OrdersByCalculationPage = () => {
    const { excelId } = useParams();
    const { translations } = useContext(LanguageContext) || {};
    const queryClient = useQueryClient();

    const [success, setSuccess] = useState("");
    const [error, setError] = useState("");
    const [confirm, setConfirm] = useState(null);

    const { data, isLoading } = useQuery({
        queryKey: ["getOrdersByIdCalculation", excelId],
        queryFn: () => getOrderByCalculation(excelId),
        staleTime: 5 * 60 * 1000,
    });

    const mutation = useMutation({
        mutationFn: (formData) => deleteCreatedOrder(formData),
        onSuccess: (response) => {
            queryClient.invalidateQueries([
                "getOrdersByIdCalculation",
                excelId,
            ]);
            setSuccess(response.data.message);
            setError("");
            setConfirm(null);
        },
        onError: (err) => {
            setSuccess("");
            setError(err.response?.data?.message || "");
        },
    });

    const handleDelete = useCallback(() => {
        if (!confirm?.id) return;
        const formData = new FormData();
        formData.append("id", confirm.id);
        mutation.mutateAsync(formData).catch(() => {});
    }, [confirm, mutation]);

    const rows = useMemo(() => data?.data || [], [data]);

    const columns = useMemo(
        () => [
            {
                field: "id",
                headerName: translations?.adminAcquisitionPageColumnID || "ID",
                width: 70,
                align: "center",
                headerAlign: "center",
                renderCell: (p) => (
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontFamily: "monospace" }}
                    >
                        {p.value}
                    </Typography>
                ),
            },
            {
                field: "status",
                headerName: "Status",
                minWidth: 140,
                align: "center",
                headerAlign: "center",
                renderCell: (p) => {
                    const sent = Number(p.row.status) === 1;
                    return (
                        <StatusBadge
                            status={sent ? "active" : "pending"}
                            label={
                                sent
                                    ? translations?.statusSent || "Poslato"
                                    : translations?.statusWaiting || "Na čekanju"
                            }
                        />
                    );
                },
            },
            {
                field: "supplier_name",
                headerName: translations?.supplier || "Dobavljač",
                minWidth: 200,
                flex: 1,
                renderCell: (p) => (
                    <Tooltip
                        arrow
                        title={`${
                            translations?.supplierCode || "Šifra"
                        }: ${p.row.supplier_code}`}
                    >
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: 500,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {p.row.supplier_name}
                        </Typography>
                    </Tooltip>
                ),
            },
            {
                field: "document_number",
                headerName:
                    translations?.poslovodjaCreatedOrdersCode ||
                    "Broj dokumenta",
                minWidth: 200,
                flex: 1,
                renderCell: (p) => (
                    <Chip
                        label={p.row.document_number}
                        size="small"
                        variant="outlined"
                        sx={{ fontFamily: "monospace", fontWeight: 600 }}
                    />
                ),
            },
            {
                field: "warehouse_code",
                headerName:
                    translations?.poslovodjaLagerstore_code || "Magacin",
                width: 110,
                align: "center",
                headerAlign: "center",
            },
            {
                field: "total_items",
                headerName: translations?.totalItems || "Stavke",
                width: 110,
                align: "center",
                headerAlign: "center",
                renderCell: (p) => (
                    <Chip
                        size="small"
                        label={p.row.total_items}
                        sx={{
                            fontFamily: "monospace",
                            fontWeight: 700,
                            bgcolor: "primary.main",
                            color: "primary.contrastText",
                        }}
                    />
                ),
            },
            {
                field: "actions",
                headerName: translations?.actions || "Akcije",
                minWidth: 130,
                align: "center",
                headerAlign: "center",
                sortable: false,
                filterable: false,
                renderCell: (p) => (
                    <Stack direction="row" spacing={0.5}>
                        <Tooltip title={translations?.information || "Detalji"}>
                            <IconButton
                                component={RouterLink}
                                to={`/narudzbina/${p.row.id}`}
                                size="small"
                            >
                                <InfoOutlinedIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={translations?.delete || "Obriši"}>
                            <IconButton
                                size="small"
                                color="error"
                                onClick={() =>
                                    setConfirm({
                                        id: p.row.id,
                                        document_number: p.row.document_number,
                                    })
                                }
                            >
                                <DeleteOutlineRoundedIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                ),
            },
        ],
        [translations]
    );

    return (
        <Box>
            <PageHeader
                icon={<AssignmentOutlinedIcon />}
                eyebrow={translations?.orders || "Narudžbine"}
                title={
                    translations?.ordersByCalculation ||
                    "Narudžbenice za kalkulaciju"
                }
                description={
                    translations?.ordersByCalculationSubtitle ||
                    "Pregled svih narudžbenica generisanih za ovu kalkulaciju."
                }
            />

            <CalculationTabs active="nabavka" />

            <Stack spacing={2}>
                {success && (
                    <Alert severity="success" onClose={() => setSuccess("")}>
                        {success}
                    </Alert>
                )}
                {error && (
                    <Alert severity="error" onClose={() => setError("")}>
                        {error}
                    </Alert>
                )}

                <SectionCard padded={false}>
                    <DataTable
                        rows={rows}
                        columns={columns}
                        loading={isLoading}
                        mutating={mutation.isLoading}
                        rowHeight={60}
                        emptyIcon={<AssignmentOutlinedIcon />}
                        emptyTitle={
                            translations?.noOrdersTitle ||
                            "Nema kreiranih narudžbenica"
                        }
                    />
                </SectionCard>
            </Stack>

            <Dialog
                open={Boolean(confirm)}
                onClose={() => setConfirm(null)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle sx={{ pb: 1 }}>
                    {translations?.delete || "Obriši"} narudžbenicu
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {translations?.modalTextCreatedOrder ||
                            "Da li ste sigurni da želite da obrišete ovu narudžbenicu?"}
                    </DialogContentText>
                    {confirm && (
                        <Box sx={{ mt: 2 }}>
                            <Typography
                                variant="body2"
                                sx={{ fontFamily: "monospace" }}
                            >
                                ID: {confirm.id}
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{ fontFamily: "monospace" }}
                            >
                                #{confirm.document_number}
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirm(null)}>
                        {translations?.adminNo || "Otkaži"}
                    </Button>
                    <Button
                        onClick={handleDelete}
                        color="error"
                        variant="contained"
                        startIcon={<DeleteOutlineRoundedIcon />}
                    >
                        {translations?.adminYes || "Obriši"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default OrdersByCalculationPage;